/**
 * evaluateMappingRules.js
 *
 * Pure, local evaluation of an AutomationBinding's `rules[]` (the
 * 'mapping' strategy's model) against a plain test value — no Jinja2, no
 * Home Assistant, no generator involvement. Mirrors the *semantics* of
 * bindingGenerators.js's generateMapping()/compareExpression() (same
 * operators, same first-match-wins order, same `when: null` default,
 * same Jinja `|float` default-to-0 behavior on non-numeric values for
 * ordering operators) so a designer's preview matches what the generated
 * Jinja2 will actually do at runtime — without ever generating or running
 * that Jinja2.
 *
 * This is the single place the rules[] matching algorithm is
 * implemented outside the generator; reuse it rather than re-deriving
 * "which rule matches" anywhere else (e.g. a future validation tool).
 */

/** @private — mirrors Jinja2's `|float` filter's silent default to 0.0 */
function toFloat(value) {
  const n = parseFloat(value);
  return Number.isNaN(n) ? 0 : n;
}

/** @private */
function ruleMatches(when, testValue) {
  if (!when) return false;
  switch (when.operator) {
    case '==': return String(testValue) === String(when.value);
    case '!=': return String(testValue) !== String(when.value);
    case '<':  return toFloat(testValue) <  toFloat(when.value);
    case '<=': return toFloat(testValue) <= toFloat(when.value);
    case '>':  return toFloat(testValue) >  toFloat(when.value);
    case '>=': return toFloat(testValue) >= toFloat(when.value);
    default:   return false;
  }
}

/**
 * Evaluate a rules[] list against one test value, first-match-wins,
 * exactly as bindingGenerators.js's generateMapping() does.
 *
 * @param {Array<{when: {operator: string, value: string}|null, result: string}>} [rules]
 * @param {string} testValue
 * @returns {{ index: number, rule: object } | null} the matching rule
 *   (a `when: null` rule always matches), or null if none matched —
 *   i.e. the same "no rule matched" case the generator falls back to
 *   binding.fallback/empty string for.
 */
export function evaluateMappingRules(rules, testValue) {
  for (let index = 0; index < (rules ?? []).length; index++) {
    const rule = rules[index];
    if (!rule.when || ruleMatches(rule.when, testValue)) {
      return { index, rule };
    }
  }
  return null;
}
